/**
 * Account Request API service
 *
 * Public endpoints for account request flow (no auth required).
 */

import { config } from '@/config'

const API_BASE_URL = config.apiUrl

// =============================================================================
// Types
// =============================================================================

export type RequestedRole = 'investor' | 'agent'

export interface VerifyEmailRequest {
  email: string
  requested_role?: RequestedRole
}

export interface VerifyEmailResponse {
  message: string
}

export interface TokenVerifyResponse {
  email: string
  requested_role: RequestedRole
}

export interface AgentQualificationData {
  license_number: string
  license_state: string
  mls_boards: string[]
}

export interface AccountRequestCreate {
  token: string
  requested_role?: RequestedRole
  first_name: string
  last_name: string
  phone: string
  company_name: string
  markets: string[]
  // Investor-only qualification fields (optional when requested_role === 'agent')
  wholesale_intent?: string | null
  proof_of_funds_available?: string | null
  proof_of_funds_file_key?: string | null
  proof_of_funds_file_name?: string | null
  hard_money_lender?: string | null
  hard_money_lender_explanation?: string | null
  annual_purchase_volume?: string | null
  referral_source?: string | null
  referral_source_detail?: string | null
  // Agent-only qualification data (required when requested_role === 'agent')
  qualification_data?: AgentQualificationData | null
  // Free-form partnership source captured at agent signup (optional)
  partnership_source?: string | null
}

export interface UploadPofResponse {
  file_key: string
  file_name: string
}

export interface AccountRequestCreateResponse {
  /** Pending account_request ID. Populated for investor submissions, which
   *  go through the legacy admin-approval flow. */
  id?: string
  /** Email address the password-setup link was sent to. Populated for agent
   *  submissions, which provision the user directly. */
  email?: string
  message: string
}

export interface ApiError {
  detail: string
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Request email verification for account request.
 *
 * Sends a verification email if the email is available.
 */
export async function verifyEmail(
  email: string,
  requestedRole: RequestedRole = 'investor'
): Promise<VerifyEmailResponse> {
  const response = await fetch(`${API_BASE_URL}/account-requests/verify-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, requested_role: requestedRole }),
  })

  if (!response.ok) {
    const error: ApiError = await response.json()
    throw new Error(error.detail || 'Failed to verify email')
  }

  return response.json()
}

/**
 * Verify an email verification token.
 *
 * Returns the email address if the token is valid.
 */
export async function verifyToken(token: string): Promise<TokenVerifyResponse> {
  const response = await fetch(`${API_BASE_URL}/account-requests/verify/${token}`)

  if (!response.ok) {
    const error: ApiError = await response.json()
    throw new Error(error.detail || 'Invalid or expired token')
  }

  return response.json()
}

/**
 * Upload a proof of funds document.
 *
 * Requires a valid email verification token.
 */
export async function uploadPof(
  token: string,
  file: File
): Promise<UploadPofResponse> {
  const formData = new FormData()
  formData.append('token', token)
  formData.append('file', file)

  const response = await fetch(`${API_BASE_URL}/account-requests/upload-pof`, {
    method: 'POST',
    body: formData,
  })

  if (!response.ok) {
    if (response.status === 413) {
      throw new Error('File too large. Maximum size is 10MB')
    }
    const error: ApiError = await response.json()
    throw new Error(error.detail || 'Failed to upload file')
  }

  return response.json()
}

/**
 * Submit an account request.
 *
 * Requires a valid email verification token.
 */
export async function submitAccountRequest(
  data: AccountRequestCreate
): Promise<AccountRequestCreateResponse> {
  const response = await fetch(`${API_BASE_URL}/account-requests`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const error: ApiError = await response.json()
    throw new Error(error.detail || 'Failed to submit account request')
  }

  return response.json()
}
